// Pure functions - NO React, NO DOM

import type { CascadeSlotConfig, GridCell, MultiplierPoolEntry } from '@core/types/cascade-slot.types';

export function getRandomInt(min: number, max: number): number {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function RandomChoose(weights: number[]): number {
  let sum = 0;
  for (let k = 0; k < weights.length; k++) {
    sum += weights[k];
  }

  let choose = getRandomInt(0, sum - 1);

  for (let k = 0; k < weights.length; k++) {
    if (choose < weights[k]) {
      return k;
    }
    choose -= weights[k];
  }

  return weights.length - 1;
}

/**
 * Place guaranteed scatter symbols in random positions on the grid.
 * Replaces non-scatter cells with 'SC' symbols.
 */
function placeGuaranteedScatters(grid: GridCell[][], count: number, scatterId: string = 'SC'): void {
  const allPositions: { row: number; col: number }[] = [];
  for (let r = 0; r < grid.length; r++) {
    for (let c = 0; c < grid[r].length; c++) {
      const sid = grid[r][c].symbolId;
      // Skip out-of-bounds empty cells and existing scatters
      if (sid === '' || sid === scatterId || sid === scatterId + '1') continue;
      allPositions.push({ row: r, col: c });
    }
  }
  // Fisher-Yates shuffle
  for (let i = allPositions.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [allPositions[i], allPositions[j]] = [allPositions[j], allPositions[i]];
  }
  const positions = allPositions.slice(0, count);
  for (const pos of positions) {
    grid[pos.row][pos.col].symbolId = scatterId;
  }
}

/** Check whether a cell position is within a reel's active area */
function isCellInReel(row: number, col: number, rows: number, reelHeights?: number[]): boolean {
  if (!reelHeights || !reelHeights[col]) return true;
  const startRow = rows - reelHeights[col];
  return row >= startRow;
}

export function generateGrid(
  config: CascadeSlotConfig,
  existingGrid?: (GridCell | null)[][],
  isFreeGame: boolean = false,
  isBuyFeature: boolean = false,
  combo: number = 0,
): GridCell[][] {
  const { rows, columns, symbols, multiplierPool, reelHeights } = config;
  const grid: (GridCell | null)[][] = existingGrid
    ? existingGrid.map(row => [...row])
    : Array.from({ length: rows }, () => Array(columns).fill(null));

  const scatterAppear: boolean[] = new Array(rows).fill(false);
  const bonusAppear: boolean[] = new Array(rows).fill(false);

  // Check existing grid for SC/BN per-row constraints
  if (existingGrid) {
    for (let i = 0; i < rows; i++) {
      for (let j = 0; j < columns; j++) {
        const cell = existingGrid[i]?.[j];
        if (cell) {
          const sym = symbols.find(s => s.id === cell.symbolId);
          if (sym?.category === 'scatter') scatterAppear[i] = true;
          if (sym?.category === 'bonus') bonusAppear[i] = true;
        }
      }
    }
  }

  // Fill column by column, row by row (matching reference model.ts FrameGenerator)
  for (let j = 0; j < columns; j++) {
    for (let i = 0; i < rows; i++) {
      // Skip cells outside reel bounds (for variable-height reels)
      if (!isCellInReel(i, j, rows, reelHeights)) continue;
      if (grid[i][j] !== null) continue;

      // Build weights array with per-row SC/BN exclusion
      const weights = symbols.map(s => {
        let w = isFreeGame ? s.freeGameWeight : s.weight;
        if (s.category === 'scatter' && scatterAppear[i]) w = 0;
        if (s.category === 'bonus' && bonusAppear[i]) w = 0;
        return w;
      });

      const symbolIndex = RandomChoose(weights);
      const symbol = symbols[symbolIndex];

      let multiplierValue = 0;
      if (symbol.category === 'scatter') {
        scatterAppear[i] = true;
      }
      if (symbol.category === 'bonus') {
        bonusAppear[i] = true;
        multiplierValue = resolveMultiplierValue(multiplierPool);
      }

      let finalSymbolId = symbol.id;

      // Super Scatter: upgrade scatter → scatter1 based on weighted random
      const scatterId = config.freeSpins?.triggerSymbolId || 'SC';
      if (finalSymbolId === scatterId && config.hasSuperScatter && config.superScatterRate && config.superScatterRate.length >= 2) {
        const upgradeIndex = RandomChoose(config.superScatterRate);
        if (upgradeIndex === 1) {
          finalSymbolId = scatterId + '1';
        }
      }

      grid[i][j] = {
        symbolId: finalSymbolId,
        multiplierValue,
        isWinning: false,
        isRemoving: false,
        isNew: existingGrid ? true : false,
        isFalling: false,
        fallDistance: 0,
        columnIndex: j,
      };
    }
  }

  // Fill out-of-bounds cells with empty markers (for variable-height reels)
  if (reelHeights) {
    for (let j = 0; j < columns; j++) {
      const startRow = rows - (reelHeights[j] ?? rows);
      for (let i = 0; i < startRow; i++) {
        if (grid[i][j] === null) {
          grid[i][j] = {
            symbolId: '',
            multiplierValue: 0,
            isWinning: false,
            isRemoving: false,
            isNew: false,
            isFalling: false,
            fallDistance: 0,
            columnIndex: j,
          };
        }
      }
    }
  }

  // Buy Feature: place guaranteed scatters on the first combo
  if (isBuyFeature && combo === 0 && config.buyFeatureScatterCount && config.buyFeatureScatterCount.length > 0) {
    const extraIndex = RandomChoose(config.buyFeatureScatterCount);
    const totalScatters = config.freeSpins.triggerCount + extraIndex;
    placeGuaranteedScatters(grid as GridCell[][], totalScatters, config.freeSpins?.triggerSymbolId || 'SC');
  }

  return grid as GridCell[][];
}

function resolveMultiplierValue(pool: MultiplierPoolEntry[]): number {
  const weights = pool.map(e => e.weight);
  const index = RandomChoose(weights);
  return pool[index].value;
}
